import React, { useEffect, useRef } from "react";

// Open modals, bottom to top. Escape only closes the topmost one (a modal can
// open another modal, e.g. the session roster inside the device workbench),
// and body scroll is restored only when the last modal closes.
const modalStack = [];

const Modal = ({
  show,
  onClose,
  children,
  maxWidth = "480px",
  fullScreen = false,
}) => {
  const stackToken = useRef();
  if (!stackToken.current) stackToken.current = {};

  // Close on escape key
  useEffect(() => {
    const token = stackToken.current;
    const handleEscape = (e) => {
      if (e.key === "Escape" && modalStack[modalStack.length - 1] === token) {
        onClose();
      }
    };

    if (show) {
      modalStack.push(token);
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      const i = modalStack.indexOf(token);
      if (i !== -1) modalStack.splice(i, 1);
      if (modalStack.length === 0) document.body.style.overflow = "unset";
    };
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ animation: "fadeIn 0.2s ease-out" }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 backdrop-blur-sm"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`relative flex flex-col overflow-hidden bg-white ${
          fullScreen
            ? "h-full w-full rounded-none"
            : "max-h-[90vh] rounded-[20px]"
        }`}
        style={{
          ...(fullScreen ? {} : { maxWidth, width: "calc(100% - 32px)" }),
          boxShadow: fullScreen ? "none" : "0 20px 60px rgba(0, 0, 0, 0.2)",
          animation: "modalSlideIn 0.25s ease-out",
        }}
      >
        {children}
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Modal;
